import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { adminApi, uploadFileToSignedUrl } from '../lib/api.ts';
import { Avatar } from '../components/Avatar.tsx';
import { Btn } from '../components/Btn.tsx';
import { ConfirmModal } from '../components/ConfirmModal.tsx';
import { CenteredSpinner } from '../components/Spinner.tsx';
import { CompanyStatusBadge, SubmissionStatusBadge } from '../components/StatusBadge.tsx';
import { ProgressBar } from '../components/ProgressBar.tsx';
import { ErrorMessage } from '../components/ErrorMessage.tsx';
import { ExpiryChip } from '../components/ExpiryChip.tsx';
import { Modal } from '../components/Modal.tsx';
import { NavIcon } from '../components/NavIcon.tsx';
import { Field, Select, TextInput } from '../components/Field.tsx';
import { FileDrop } from '../components/FileDrop.tsx';
import { fromDateInput } from '../lib/expiry.ts';
import { useBranding } from '../lib/branding.tsx';
import { companyInfoLabel, contractorTypeOptions } from '../lib/companyInfo.ts';
import { teamRegionNames, useTeamRegions } from '../lib/teamRegions.ts';
import type {
  AdminQuestionnaire,
  AdminSubmission,
  Company,
  CompanyDocument,
  Question,
  QuestionCategory,
  Submission,
  SubmissionFile,
  TrainingAttempt,
} from '../lib/types.ts';
import { computeProgress } from '../lib/visibility.ts';
import {
  buildCompanyDetailTabs,
  questionsForProgress,
  type CategorySection,
  type ComplianceDocRow,
  type QuestionnaireGroup,
} from '../lib/companyDetailTabs.ts';
import { questionnaireProgress } from '../lib/companyDetailProgress.ts';
import { instanceOptionLabel } from '../lib/instanceOption.ts';
import { answerOptionPills, type AnswerOptionPill } from '../lib/answerOptions.ts';
import { useReviewMutation } from './useReviewMutation.ts';
import { useAbnLookup } from '../lib/useAbnLookup.ts';
import { AbnStatusNote } from '../company/CompanyInfo.tsx';
import { Tabs, type TabItem } from '../components/Tabs.tsx';
import { CommentModal } from './CommentModal.tsx';
import { NotCompliantModal } from './NotCompliantModal.tsx';
import { DocumentReviewTable } from './DocumentReviewTable.tsx';
import { ComplianceDocStats } from './ComplianceDocStats.tsx';
import { TrainingAttemptList } from './TrainingAttemptList.tsx';

// Synthetic keys for the fixed tabs. Namespaced with underscores so they can
// never collide with a real questionnaire UUID.
const COMPANY_INFO_TAB = '__company_info__';
const COMPLIANCE_DOCS_TAB = '__compliance_docs__';
const OLD_QUESTIONNAIRES_TAB = '__old_questionnaires__';
const ADDITIONAL_TAB = '__additional_questions__';

export function AdminCompanyDetail() {
  const { id = '' } = useParams();
  const qc = useQueryClient();
  const [notifiedAt, setNotifiedAt] = useState<number | null>(null);

  const companyQuery = useQuery({
    queryKey: ['company', id],
    queryFn: () => adminApi.get<{ company: Company }>(`/api/companies/${id}`),
    enabled: !!id,
  });
  const submissionsQuery = useQuery({
    queryKey: ['company', id, 'submissions'],
    queryFn: () =>
      adminApi.get<{ submissions: AdminSubmission[] }>(`/api/companies/${id}/submissions`),
    enabled: !!id,
  });
  const attemptsQuery = useQuery({
    queryKey: ['company', id, 'training-attempts'],
    queryFn: () =>
      adminApi.get<{ training_attempts: TrainingAttempt[] }>(
        `/api/companies/${id}/training-attempts`,
      ),
    enabled: !!id,
  });
  const questionsQuery = useQuery({
    queryKey: ['questions'],
    queryFn: () => adminApi.get<{ questions: Question[] }>('/api/questions'),
  });
  const documentsQuery = useQuery({
    queryKey: ['documents'],
    queryFn: () => adminApi.get<{ documents: CompanyDocument[] }>('/api/documents'),
  });
  const categoriesQuery = useQuery({
    queryKey: ['question-categories'],
    queryFn: () => adminApi.get<{ categories: QuestionCategory[] }>('/api/question-categories'),
  });
  const questionnairesQuery = useQuery({
    queryKey: ['company', id, 'questionnaires'],
    queryFn: () =>
      adminApi.get<{ questionnaires: AdminQuestionnaire[] }>(
        `/api/companies/${id}/questionnaires`,
      ),
    enabled: !!id,
  });

  const inviteMutation = useMutation({
    mutationFn: () => adminApi.post(`/api/companies/${id}/invite`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['company', id] }),
  });

  const [confirmOpen, setConfirmOpen] = useState(false);
  const deactivateMutation = useMutation({
    mutationFn: () => adminApi.post<{ company: Company }>(`/api/companies/${id}/deactivate`),
    onSuccess: () => {
      setConfirmOpen(false);
      void qc.invalidateQueries({ queryKey: ['company', id] });
      void qc.invalidateQueries({ queryKey: ['companies'] });
    },
  });
  const reactivateMutation = useMutation({
    mutationFn: () => adminApi.post<{ company: Company }>(`/api/companies/${id}/reactivate`),
    onSuccess: () => {
      setConfirmOpen(false);
      void qc.invalidateQueries({ queryKey: ['company', id] });
      void qc.invalidateQueries({ queryKey: ['companies'] });
    },
  });

  const pendingChangesQuery = useQuery({
    queryKey: ['company', id, 'pending-changes'],
    queryFn: () => adminApi.get<{ count: number }>(`/api/companies/${id}/notify-changes`),
    enabled: !!id,
  });

  const notifyChangesMutation = useMutation({
    mutationFn: () =>
      adminApi.post<{ sent: boolean; count: number; to?: string }>(
        `/api/companies/${id}/notify-changes`,
      ),
    onSuccess: () => {
      setNotifiedAt(Date.now());
      void qc.invalidateQueries({ queryKey: ['company', id, 'pending-changes'] });
    },
  });

  const pendingChangesCount = pendingChangesQuery.data?.count ?? 0;

  const documents = documentsQuery.data?.documents ?? [];
  const docMap = useMemo(() => {
    const m = new Map<string, CompanyDocument>();
    for (const d of documents) m.set(d.id, d);
    return m;
  }, [documents]);
  const documentName = (docId: string) => docMap.get(docId)?.name ?? docId.slice(0, 8);

  const submissions = submissionsQuery.data?.submissions ?? [];
  const trainingAttempts = attemptsQuery.data?.training_attempts ?? [];
  const questions = questionsQuery.data?.questions ?? [];
  const subByQ = useMemo(() => {
    const m = new Map<string, AdminSubmission>();
    for (const s of submissions) m.set(s.question_id, s);
    return m;
  }, [submissions]);

  const questionById = useMemo(() => new Map(questions.map((x) => [x.id, x])), [questions]);

  const categories = categoriesQuery.data?.categories ?? [];
  const questionnaires = questionnairesQuery.data?.questionnaires ?? [];

  // Progress counts only questions the company can actually be asked —
  // applicable questionnaires plus orphans — matching the company portal's
  // server-side computation (see questionsForProgress).
  const progressQuestions = useMemo(
    () => questionsForProgress(questionnaires, categories, questions),
    [questionnaires, categories, questions],
  );
  const progress = useMemo(
    () => computeProgress(progressQuestions, submissions as unknown as Submission[]),
    [progressQuestions, submissions],
  );

  const answeredIds = useMemo(() => new Set(submissions.map((s) => s.question_id)), [submissions]);

  // Tab model: applicable questionnaires become tabs (category sections of
  // non-doc questions), retired-but-answered questionnaires collapse into one
  // Old Questionnaires bucket, and every document_upload question lands in
  // the Compliance Documents rows.
  const tabModel = useMemo(
    () =>
      buildCompanyDetailTabs(
        questionnaires,
        categories,
        questions,
        progress.visibleIds,
        answeredIds,
      ),
    [questionnaires, categories, questions, progress.visibleIds, answeredIds],
  );
  const [activeTab, setActiveTab] = useState<string>(COMPANY_INFO_TAB);
  const [uploadDocOpen, setUploadDocOpen] = useState(false);

  if (
    companyQuery.isLoading ||
    submissionsQuery.isLoading ||
    questionsQuery.isLoading ||
    categoriesQuery.isLoading ||
    documentsQuery.isLoading ||
    questionnairesQuery.isLoading
  ) {
    return <CenteredSpinner />;
  }
  const company = companyQuery.data?.company;
  if (!company) {
    return <ErrorMessage error={new Error('Company not found')} />;
  }

  const isDeactivated = company.status === 'deactivated';

  // "Company information" leads the strip, then one tab per applicable
  // questionnaire, then Compliance Documents (always), then conditional
  // Old Questionnaires / Additional questions tabs.
  const tabItems: TabItem[] = [
    { key: COMPANY_INFO_TAB, label: 'Company information' },
    ...tabModel.questionnaireTabs.map((g) => ({
      key: g.questionnaire.id,
      label: g.questionnaire.name,
      badge: pendingDecisionCount(groupQuestions(g), subByQ),
    })),
    {
      key: COMPLIANCE_DOCS_TAB,
      label: 'Compliance Documents',
      badge: pendingDecisionCount(
        tabModel.complianceDocs.map((r) => r.question),
        subByQ,
      ),
    },
    ...(tabModel.oldGroups.length > 0
      ? [
          {
            key: OLD_QUESTIONNAIRES_TAB,
            label: 'Old Questionnaires',
            badge: pendingDecisionCount(tabModel.oldGroups.flatMap(groupQuestions), subByQ),
          },
        ]
      : []),
    ...(tabModel.orphanQuestions.length > 0
      ? [
          {
            key: ADDITIONAL_TAB,
            label: 'Additional questions',
            badge: pendingDecisionCount(tabModel.orphanQuestions, subByQ),
          },
        ]
      : []),
  ];
  // Fall back to Company information when the stored key no longer matches a
  // tab (e.g. a questionnaire was deactivated between refetches).
  const activeKey = tabItems.some((t) => t.key === activeTab) ? activeTab : COMPANY_INFO_TAB;
  const activeGroup = tabModel.questionnaireTabs.find((g) => g.questionnaire.id === activeKey);

  return (
    <div className="space-y-6">
      <div className="space-y-3">
        <Link to="/admin/companies" className="text-sm text-slate-500 hover:text-slate-700">
          ← Companies
        </Link>

        {/* Header row: identity on the left, primary actions aligned on the
            right of the same row. */}
        <div className="flex flex-wrap items-center justify-between gap-4">
          {/* Identity: avatar, then the company name with its status beside it
              and the primary contact + email on the line underneath. */}
          <div className="flex items-start gap-4">
            <Avatar name={company.company_name} size={56} />
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-3">
                <h1 className="text-2xl font-semibold text-slate-900">{company.company_name}</h1>
                <CompanyStatusBadge status={company.status} />
              </div>
              <div className="mt-1 text-sm text-slate-600">
                <span className="font-medium text-slate-700">{company.contact_name}</span>
                <span className="mx-2 text-slate-300">·</span>
                <a
                  href={`mailto:${company.email}`}
                  className="text-slate-500 hover:text-slate-700 hover:underline"
                >
                  {company.email}
                </a>
              </div>
            </div>
          </div>

          {/* Actions, aligned with the company name and avatar. */}
          <div className="flex flex-wrap items-center justify-end gap-2">
            {notifiedAt !== null && pendingChangesCount === 0 && (
              <span className="text-xs text-emerald-600">Changes sent ✓</span>
            )}
            {!isDeactivated && (
              <>
                {/* All lifecycle actions pause while deactivated (the API 409s them
                    anyway — this keeps the UI honest). */}
                <Btn
                  size="sm"
                  variant="secondary"
                  onClick={() => inviteMutation.mutate()}
                  loading={inviteMutation.isPending}
                >
                  {company.invited_at ? 'Resend invite' : 'Send invite'}
                </Btn>
                {pendingChangesCount > 0 && (
                  <Btn
                    size="sm"
                    variant="secondary"
                    onClick={() => notifyChangesMutation.mutate()}
                    loading={notifyChangesMutation.isPending}
                  >
                    Notify company of changes ({pendingChangesCount})
                  </Btn>
                )}
              </>
            )}
          </div>
        </div>
      </div>
      {notifyChangesMutation.error && <ErrorMessage error={notifyChangesMutation.error} />}

      <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
        <Tabs
          tabs={tabItems}
          active={activeKey}
          onSelect={setActiveTab}
          ariaLabel="Company review sections"
          className="px-2 pt-2"
        />
        <div className="p-6">
          {activeGroup ? (
            <QuestionnaireTabPanel
              key={activeGroup.questionnaire.id}
              group={activeGroup}
              invitedAt={company.invited_at}
              visibleIds={progress.visibleIds}
              subByQ={subByQ}
              companyId={id}
              documentName={documentName}
              documents={documents}
              questionById={questionById}
            />
          ) : activeKey === COMPLIANCE_DOCS_TAB ? (
            <div
              role="tabpanel"
              id={`tabpanel-${COMPLIANCE_DOCS_TAB}`}
              aria-labelledby={`tab-${COMPLIANCE_DOCS_TAB}`}
            >
              <TabHeading
                title="Compliance Documents"
                subtitle="Every document this company needs to provide, across all questionnaires."
                actions={
                  tabModel.complianceDocs.length > 0 ? (
                    <Btn size="sm" variant="secondary" onClick={() => setUploadDocOpen(true)}>
                      <NavIcon name="file-text" size={16} />
                      Upload document
                    </Btn>
                  ) : undefined
                }
              />
              {uploadDocOpen && (
                <AdminUploadDocumentModal
                  companyId={id}
                  rows={tabModel.complianceDocs}
                  onClose={() => setUploadDocOpen(false)}
                  onUploaded={() => {
                    setUploadDocOpen(false);
                    void qc.invalidateQueries({ queryKey: ['company', id, 'submissions'] });
                    void qc.invalidateQueries({ queryKey: ['company', id] });
                  }}
                />
              )}
              {tabModel.complianceDocs.length > 0 ? (
                <>
                  <ComplianceDocStats
                    submissions={tabModel.complianceDocs.map((r) => subByQ.get(r.question.id))}
                  />
                  <DocumentReviewTable
                    rows={tabModel.complianceDocs}
                    subByQ={subByQ}
                    companyId={id}
                    documents={documents}
                    documentName={documentName}
                    questionById={questionById}
                  />
                </>
              ) : (
                <p className="text-sm italic text-slate-400">
                  No document requirements for this company.
                </p>
              )}
            </div>
          ) : activeKey === OLD_QUESTIONNAIRES_TAB ? (
            <div
              role="tabpanel"
              id={`tabpanel-${OLD_QUESTIONNAIRES_TAB}`}
              aria-labelledby={`tab-${OLD_QUESTIONNAIRES_TAB}`}
            >
              <TabHeading
                title="Old Questionnaires"
                subtitle="Questionnaires that no longer apply to this company — showing previously submitted answers."
              />
              <div className="space-y-10">
                {tabModel.oldGroups.map((g) => (
                  <div key={g.questionnaire.id}>
                    <h3 className="mb-3 text-lg font-semibold text-slate-900">
                      {g.questionnaire.name}
                    </h3>
                    <CategorySections
                      sections={g.sections}
                      subByQ={subByQ}
                      companyId={id}
                      documentName={documentName}
                      documents={documents}
                      questionById={questionById}
                      categoryHeadingLevel="h4"
                    />
                  </div>
                ))}
              </div>
            </div>
          ) : activeKey === ADDITIONAL_TAB ? (
            <div
              role="tabpanel"
              id={`tabpanel-${ADDITIONAL_TAB}`}
              aria-labelledby={`tab-${ADDITIONAL_TAB}`}
            >
              <TabHeading
                title="Additional questions"
                subtitle="Questions whose category no longer exists."
              />
              <div className="divide-y divide-slate-200">
                {tabModel.orphanQuestions.map((question) => (
                  <SubmissionRow
                    key={question.id}
                    question={question}
                    submission={subByQ.get(question.id)}
                    documentName={documentName}
                    companyId={id}
                    documents={documents}
                    instanceOption={instanceOptionLabel(question, questionById)}
                  />
                ))}
              </div>
            </div>
          ) : (
            <div
              role="tabpanel"
              id={`tabpanel-${COMPANY_INFO_TAB}`}
              aria-labelledby={`tab-${COMPANY_INFO_TAB}`}
            >
              <CompanyInfoPanel company={company} />
            </div>
          )}
        </div>
      </div>

      <TrainingAttemptList attempts={trainingAttempts} />

      <div className="border-t border-slate-200 pt-6">
        <h3 className="text-sm font-semibold uppercase tracking-wide text-red-600">Danger zone</h3>
        {isDeactivated ? (
          <div className="mt-3 flex items-center justify-between rounded-xl border border-slate-200 bg-slate-50 p-4">
            <div className="text-sm text-slate-700">
              This company is deactivated. Reactivate to restore their previous status and portal
              access.
            </div>
            <Btn onClick={() => setConfirmOpen(true)} loading={reactivateMutation.isPending}>
              Reactivate company
            </Btn>
          </div>
        ) : (
          <div className="mt-3 flex items-center justify-between rounded-xl border border-red-200 bg-red-50 p-4">
            <div className="text-sm text-red-700">
              Deactivate this company. They&apos;ll be hidden from the companies list and lose portal
              access. Records are kept — you can reactivate them anytime.
            </div>
            <Btn
              variant="danger"
              onClick={() => setConfirmOpen(true)}
              loading={deactivateMutation.isPending}
            >
              Deactivate company
            </Btn>
          </div>
        )}
      </div>

      <ConfirmModal
        open={confirmOpen}
        title={isDeactivated ? 'Reactivate company' : 'Deactivate company'}
        confirmLabel={isDeactivated ? 'Reactivate' : 'Deactivate'}
        danger={!isDeactivated}
        loading={isDeactivated ? reactivateMutation.isPending : deactivateMutation.isPending}
        onConfirm={() =>
          isDeactivated ? reactivateMutation.mutate() : deactivateMutation.mutate()
        }
        onClose={() => {
          setConfirmOpen(false);
          deactivateMutation.reset();
          reactivateMutation.reset();
        }}
      >
        {isDeactivated ? (
          <>
            Are you sure you want to reactivate{' '}
            <span className="font-semibold">{company.company_name}</span>? They&apos;ll return to
            their previous status and regain portal access.
          </>
        ) : (
          <>
            Are you sure you want to deactivate{' '}
            <span className="font-semibold">{company.company_name}</span>? They&apos;ll be removed
            from the companies list and lose portal access. Their records are kept — you can
            reactivate them anytime.
          </>
        )}
        {(deactivateMutation.error ?? reactivateMutation.error) != null && (
          <div className="mt-3">
            <ErrorMessage error={deactivateMutation.error ?? reactivateMutation.error} />
          </div>
        )}
      </ConfirmModal>
    </div>
  );
}

// Heading shown at the top of each tab panel. The Company information tab also
// passes a subtitle and panel-level actions (Edit / Save / Cancel); category
// tabs pass the category name as the title only.
function TabHeading({
  title,
  subtitle,
  actions,
}: {
  title: string;
  subtitle?: string;
  actions?: ReactNode;
}) {
  return (
    <div className="mb-5 flex flex-wrap items-start justify-between gap-3">
      <div>
        <h2 className="text-xl font-semibold text-slate-900">{title}</h2>
        {subtitle && <p className="mt-1 text-sm text-slate-500">{subtitle}</p>}
      </div>
      {actions && <div className="flex shrink-0 items-center gap-2">{actions}</div>}
    </div>
  );
}

// One review row: a type tile, the question with its answer/files, then the
// status and per-item actions on the right. The request-changes form (comment
// + reference doc) lives in a modal.
function SubmissionRow({
  question,
  submission,
  documentName,
  companyId,
  documents,
  instanceOption,
}: {
  question: Question;
  submission: AdminSubmission | undefined;
  documentName: (id: string) => string;
  companyId: string;
  documents: CompanyDocument[];
  instanceOption: string | null;
}) {
  const [rejectOpen, setRejectOpen] = useState(false);
  const [commentOpen, setCommentOpen] = useState(false);

  function closeReject() {
    setRejectOpen(false);
    reviewMutation.reset();
  }

  function closeComment() {
    setCommentOpen(false);
    reviewMutation.reset();
  }

  const reviewMutation = useReviewMutation(companyId, submission?.id, () => {
    closeReject();
    closeComment();
  });

  const files = submission?.submission_files ?? [];

  return (
    <div className="flex items-center gap-3 py-3">
      <div className="min-w-0 flex-1">
        {submission && (
          <div className="mb-1">
            <SubmissionStatusBadge status={submission.status} />
          </div>
        )}
        <div className="text-sm font-semibold text-slate-900">
          {question.label}
          {instanceOption && (
            <span className="ml-2 rounded-full bg-indigo-50 px-2 py-0.5 text-xs font-normal text-indigo-700">
              For: {instanceOption}
            </span>
          )}
        </div>
        {question.help_text && (
          <div className="mt-0.5 text-xs text-slate-500">{question.help_text}</div>
        )}
        <div className="mt-0.5 flex flex-wrap items-start gap-x-2 gap-y-1 text-sm text-slate-600">
          <AnswerPreview question={question} submission={submission} documentName={documentName} />
          {submission && files.length > 0 && (
            <div className="flex w-full flex-wrap items-start gap-x-2 gap-y-1">
              {files.map((f) => (
                <SubmissionFileItem
                  key={f.id}
                  companyId={companyId}
                  submissionId={submission.id}
                  file={f}
                />
              ))}
            </div>
          )}
        </div>
        {submission?.reviewer_comment && (
          <div className="mt-2 rounded-md border border-orange-200 bg-orange-50 px-3 py-2 text-xs text-orange-800">
            <div className="font-medium">Reviewer note</div>
            <div>{submission.reviewer_comment}</div>
          </div>
        )}
        {/* Approve/Disapprove errors show here; reject errors show inside the modal. */}
        {!rejectOpen && !commentOpen && reviewMutation.error && (
          <div className="mt-2">
            <ErrorMessage error={reviewMutation.error} />
          </div>
        )}
      </div>

      <div className="flex shrink-0 items-center gap-2">
        {submission &&
          (() => {
            const status = submission.status;
            const isApproved = status === 'approved';
            const isRejected = status === 'rejected';
            const isNa = status === 'not_applicable';
            const dec = reviewMutation.variables?.decision;
            const busy = reviewMutation.isPending;
            // Show the spinner on the button that initiated the in-flight call —
            // a reset originates from whichever status is currently active.
            const loadingFor = (target: 'approve' | 'reject' | 'na', active: boolean) =>
              busy && (dec === target || (dec === 'reset' && active));
            return (
              <>
                <Btn
                  size="sm"
                  variant={isApproved ? 'success' : 'secondary'}
                  aria-pressed={isApproved}
                  onClick={() =>
                    reviewMutation.mutate({ decision: isApproved ? 'reset' : 'approve' })
                  }
                  loading={loadingFor('approve', isApproved)}
                >
                  <NavIcon name="check-circle" size={16} />
                  Compliant
                </Btn>
                <Btn
                  size="sm"
                  variant={isRejected ? 'danger' : 'secondary'}
                  aria-pressed={isRejected}
                  onClick={() =>
                    isRejected ? reviewMutation.mutate({ decision: 'reset' }) : setRejectOpen(true)
                  }
                  loading={loadingFor('reject', isRejected)}
                >
                  <NavIcon name="x-circle" size={16} />
                  Not Compliant
                </Btn>
                <Btn
                  size="sm"
                  variant={isNa ? 'warning' : 'secondary'}
                  aria-pressed={isNa}
                  onClick={() => reviewMutation.mutate({ decision: isNa ? 'reset' : 'na' })}
                  loading={loadingFor('na', isNa)}
                >
                  N/A
                </Btn>
                <Btn
                  size="sm"
                  variant="secondary"
                  aria-label={`Add comment for "${question.label}"`}
                  onClick={() => setCommentOpen(true)}
                >
                  <NavIcon name="edit" size={16} />
                  Comments
                </Btn>
              </>
            );
          })()}
      </div>

      {rejectOpen && (
        <NotCompliantModal
          question={question}
          documents={documents}
          pending={reviewMutation.isPending}
          error={reviewMutation.error}
          onClose={closeReject}
          onSubmit={(payload) => reviewMutation.mutate({ decision: 'reject', ...payload })}
        />
      )}

      {commentOpen && (
        <CommentModal
          question={question}
          pending={reviewMutation.isPending}
          error={reviewMutation.variables?.decision === 'comment' ? reviewMutation.error : null}
          onClose={closeComment}
          onSubmit={(comment) => reviewMutation.mutate({ decision: 'comment', comment })}
        />
      )}
    </div>
  );
}

// All non-doc questions of one questionnaire group, used for tab badges.
function groupQuestions(g: QuestionnaireGroup): Question[] {
  return g.sections.flatMap((s) => s.questions);
}

// Count submissions still needing a reviewer decision (pending or rejected) —
// drives the tab badges.
function pendingDecisionCount(
  questions: Question[],
  subByQ: Map<string, AdminSubmission>,
): number {
  return questions.reduce((n, q) => {
    const sub = subByQ.get(q.id);
    return sub && (sub.status === 'pending' || sub.status === 'rejected') ? n + 1 : n;
  }, 0);
}

// Category sections for one questionnaire tab: one bordered card per category
// with a tinted header band (same idiom as the Compliance Documents table
// header), then the standard review rows. Document-upload questions are not
// rendered here — they live in the Compliance Documents tab.
function CategorySections({
  sections,
  subByQ,
  companyId,
  documentName,
  documents,
  questionById,
  categoryHeadingLevel: CategoryHeading = 'h3',
}: {
  sections: CategorySection[];
  subByQ: Map<string, AdminSubmission>;
  companyId: string;
  documentName: (id: string) => string;
  documents: CompanyDocument[];
  questionById: Map<string, Question>;
  // 'h4' when nested under a per-questionnaire heading (Old Questionnaires tab).
  categoryHeadingLevel?: 'h3' | 'h4';
}) {
  return (
    <div className="space-y-4">
      {sections.map((s) => (
        <section key={s.category.id} className="overflow-hidden rounded-lg border border-slate-200">
          <CategoryHeading className="border-b border-slate-200 bg-slate-50 px-4 py-2 text-xs font-medium uppercase tracking-wide text-slate-500">
            {s.category.name}
          </CategoryHeading>
          <div className="divide-y divide-slate-200 px-4">
            {s.questions.map((question) => (
              <SubmissionRow
                key={question.id}
                question={question}
                submission={subByQ.get(question.id)}
                documentName={documentName}
                companyId={companyId}
                documents={documents}
                instanceOption={instanceOptionLabel(question, questionById)}
              />
            ))}
          </div>
        </section>
      ))}
    </div>
  );
}

// One applicable questionnaire's tab panel. Leads with a per-questionnaire
// Progress + Invited header block (computed from this questionnaire's visible
// required questions only), then presents the questionnaire's categories as a
// horizontal sub-tab bar with the selected category's review rows below —
// replacing the older stacked-cards layout. Sub-tab keys are namespaced with a
// "cat:" prefix so their generated tab/tabpanel ids never collide with the
// outer questionnaire tabs.
function QuestionnaireTabPanel({
  group,
  invitedAt,
  visibleIds,
  subByQ,
  companyId,
  documentName,
  documents,
  questionById,
}: {
  group: QuestionnaireGroup;
  invitedAt: string | null;
  visibleIds: Set<string>;
  subByQ: Map<string, AdminSubmission>;
  companyId: string;
  documentName: (id: string) => string;
  documents: CompanyDocument[];
  questionById: Map<string, Question>;
}) {
  const prog = questionnaireProgress(group, visibleIds, subByQ);

  const subTabKey = (categoryId: string) => `cat:${categoryId}`;
  const subTabs: TabItem[] = group.sections.map((s) => ({
    key: subTabKey(s.category.id),
    label: s.category.name,
    badge: pendingDecisionCount(s.questions, subByQ),
  }));

  // Default to the first category; reset when switching questionnaires (a new
  // group remounts this component because activeGroup keys by questionnaire.id
  // via the parent's render — but guard anyway against a stale key).
  const [activeSub, setActiveSub] = useState<string>(subTabs[0]?.key ?? '');
  const activeKey = subTabs.some((t) => t.key === activeSub) ? activeSub : (subTabs[0]?.key ?? '');
  const activeSection = group.sections.find((s) => subTabKey(s.category.id) === activeKey);

  return (
    <div
      role="tabpanel"
      id={`tabpanel-${group.questionnaire.id}`}
      aria-labelledby={`tab-${group.questionnaire.id}`}
    >
      <TabHeading title={group.questionnaire.name} />

      {/* Per-questionnaire status strip: progress scoped to this
          questionnaire's visible required questions, plus the (shared) invite
          timestamp. */}
      <div className="mb-5 grid gap-4 sm:grid-cols-2">
        <div className="rounded-xl border border-slate-200 bg-white p-4">
          <div className="text-xs uppercase tracking-wide text-slate-500">Progress</div>
          <div className="mt-2">
            <ProgressBar value={prog.percent} />
          </div>
          <div className="mt-1 text-xs text-slate-500">
            {prog.completedRequired} / {prog.totalRequired} required complete
          </div>
        </div>
        <div className="rounded-xl border border-slate-200 bg-white p-4">
          <div className="text-xs uppercase tracking-wide text-slate-500">Invited</div>
          <div className="mt-2 text-sm text-slate-700">
            {invitedAt ? new Date(invitedAt).toLocaleString() : '—'}
          </div>
        </div>
      </div>

      {/* Categories as sub-tabs (one active category panel at a time). */}
      <Tabs
        tabs={subTabs}
        active={activeKey}
        onSelect={setActiveSub}
        ariaLabel={`${group.questionnaire.name} categories`}
      />
      {activeSection && (
        <div
          role="tabpanel"
          id={`tabpanel-${activeKey}`}
          aria-labelledby={`tab-${activeKey}`}
          className="pt-4"
        >
          <div className="divide-y divide-slate-200">
            {activeSection.questions.map((question) => (
              <SubmissionRow
                key={question.id}
                question={question}
                submission={subByQ.get(question.id)}
                documentName={documentName}
                companyId={companyId}
                documents={documents}
                instanceOption={instanceOptionLabel(question, questionById)}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// Admin "upload on behalf of company" modal for the Compliance Documents tab.
// Requires picking one of the company's compliance-doc questions and a file,
// then runs the standard 3-step upload (presign → PUT → register) against the
// admin endpoints and lets the caller invalidate the page queries so the new
// file appears in the review table.
function AdminUploadDocumentModal({
  companyId,
  rows,
  onClose,
  onUploaded,
}: {
  companyId: string;
  rows: ComplianceDocRow[];
  onClose: () => void;
  onUploaded: () => void;
}) {
  const [questionId, setQuestionId] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [expiry, setExpiry] = useState('');
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<unknown>(null);

  // Group the question options by category name for a scannable dropdown; the
  // rows already arrive in questionnaire → category → question order.
  const groups: { categoryName: string; questions: Question[] }[] = [];
  for (const { question, categoryName } of rows) {
    const last = groups[groups.length - 1];
    if (last && last.categoryName === categoryName) last.questions.push(question);
    else groups.push({ categoryName, questions: [question] });
  }

  async function submit() {
    if (!questionId || !file || uploading) return;
    setUploading(true);
    setError(null);
    try {
      const mimeType = file.type || 'application/octet-stream';
      const presigned = await adminApi.post<{ path: string; url: string }>(
        `/api/companies/${companyId}/submissions/${questionId}/upload-url`,
        { file_name: file.name, mime_type: mimeType },
      );
      await uploadFileToSignedUrl(presigned.url, file);
      await adminApi.post(`/api/companies/${companyId}/submissions/${questionId}/files`, {
        file_path: presigned.path,
        file_name: file.name,
        size_bytes: file.size,
        mime_type: mimeType,
        expires_at: fromDateInput(expiry),
      });
      onUploaded();
    } catch (err) {
      setError(err);
    } finally {
      setUploading(false);
    }
  }

  return (
    <Modal
      open
      title="Upload document on behalf of company"
      size="md"
      onClose={onClose}
      footer={
        <>
          <Btn size="sm" variant="ghost" onClick={onClose} disabled={uploading}>
            Cancel
          </Btn>
          <Btn size="sm" onClick={() => void submit()} disabled={!questionId || !file} loading={uploading}>
            Upload
          </Btn>
        </>
      }
    >
      <div className="space-y-3">
        <Field label="Related document requirement" required>
          <Select
            value={questionId}
            onChange={(e) => setQuestionId(e.target.value)}
            disabled={uploading}
          >
            <option value="">— Select a document —</option>
            {groups.map((g) => (
              <optgroup key={g.categoryName} label={g.categoryName}>
                {g.questions.map((q) => (
                  <option key={q.id} value={q.id}>
                    {q.label}
                  </option>
                ))}
              </optgroup>
            ))}
          </Select>
        </Field>
        <Field label="File" required>
          <FileDrop
            multiple={false}
            disabled={uploading}
            onFiles={(list) => {
              const f = list[0];
              if (f) setFile(f);
            }}
            hint={file ? `Selected: ${file.name}` : uploading ? 'Uploading…' : undefined}
          />
        </Field>
        <Field
          label="Expiry date (optional)"
          hint="Leave blank if this document doesn't expire."
        >
          <TextInput
            type="date"
            value={expiry}
            onChange={(e) => setExpiry(e.target.value)}
            disabled={uploading}
          />
        </Field>
        {error != null && <ErrorMessage error={error} />}
      </div>
    </Modal>
  );
}

// One submitted file shown as an inline link. Images open in a modal lightbox;
// other file types open in a new tab. The signed URL is fetched on demand.
function SubmissionFileItem({
  companyId,
  submissionId,
  file,
}: {
  companyId: string;
  submissionId: string;
  file: SubmissionFile;
}) {
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const isImage = file.mime_type.startsWith('image/');
  const downloadPath = `/api/companies/${companyId}/submissions/${submissionId}/files/${file.id}/download-url`;

  // No thumbnail to populate, so the short-lived signed URL is only fetched when
  // the lightbox is actually opened.
  const urlQuery = useQuery({
    queryKey: ['submission-file-url', submissionId, file.id],
    queryFn: () => adminApi.get<{ url: string }>(downloadPath),
    enabled: isImage && lightboxOpen,
    staleTime: 4 * 60 * 1000,
  });

  async function openInNewTab() {
    const url =
      urlQuery.data?.url ??
      (await adminApi.get<{ url: string }>(downloadPath).catch(() => null))?.url;
    if (url) window.open(url, '_blank', 'noopener');
  }

  return (
    <span className="inline-flex items-center gap-1.5">
      <button
        type="button"
        onClick={() => (isImage ? setLightboxOpen(true) : void openInNewTab())}
        className="inline-flex items-center gap-1 text-xs text-blue-600 hover:underline"
      >
        <span aria-hidden="true">📎</span>
        {file.file_name}
      </button>
      <ExpiryChip expiresAt={file.expires_at} />

      {isImage && lightboxOpen && (
        <Modal
          open
          title={file.file_name}
          size="lg"
          onClose={() => setLightboxOpen(false)}
          footer={
            <>
              <Btn variant="ghost" size="sm" onClick={() => void openInNewTab()}>
                Open in new tab
              </Btn>
              <Btn variant="secondary" size="sm" onClick={() => setLightboxOpen(false)}>
                Close
              </Btn>
            </>
          }
        >
          {urlQuery.data?.url ? (
            <img
              src={urlQuery.data.url}
              alt={file.file_name}
              className="mx-auto max-h-[65vh] max-w-full rounded-md"
            />
          ) : (
            <div className="flex h-40 items-center justify-center text-sm text-slate-400">
              {urlQuery.isError ? 'Image unavailable' : 'Loading…'}
            </div>
          )}
        </Modal>
      )}
    </span>
  );
}

function AnswerPreview({
  question,
  submission,
  documentName,
}: {
  question: Question;
  submission: AdminSubmission | undefined;
  documentName: (id: string) => string;
}) {
  // Document questions are satisfied by an upload or an adoption, not a typed
  // answer, so they must never fall through to a generic "Not answered yet".
  if (question.type === 'document_adopt' || question.type === 'document_upload') {
    const adoptions = submission?.submission_adoptions ?? [];
    if (adoptions.length > 0) {
      return (
        <ul className="space-y-1">
          {adoptions.map((ad) => (
            <li key={ad.id} className="text-sm">
              {ad.mode === 'adopted' ? '✓ Adopted: ' : '↑ Uploaded: '}
              {documentName(ad.document_id)}
            </li>
          ))}
        </ul>
      );
    }
    if (question.type === 'document_upload') {
      // Uploaded file(s) render below this preview with their expiry chip.
      if ((submission?.submission_files?.length ?? 0) > 0) return null;
      return <span className="italic text-slate-400">Awaiting document</span>;
    }
    return <span className="italic text-slate-400">No adoption recorded</span>;
  }

  if (!submission) return <span className="italic text-slate-400">Not answered yet</span>;
  const a = submission.answer;
  if (question.type === 'signature') {
    if (
      a &&
      typeof a === 'object' &&
      !Array.isArray(a) &&
      typeof (a as { signature_data_url?: unknown }).signature_data_url === 'string'
    ) {
      const sig = a as { signature_data_url: string; signer_name: string; signed_at: string };
      return (
        <div className="space-y-1">
          <img
            src={sig.signature_data_url}
            alt="Signature"
            className="h-24 rounded border border-slate-200 bg-white object-contain"
          />
          <div className="text-sm text-slate-700">
            Signed by <span className="font-medium">{sig.signer_name}</span>
          </div>
          <div className="text-xs text-slate-500">{new Date(sig.signed_at).toLocaleString()}</div>
        </div>
      );
    }
    return <span className="italic text-slate-400">Not signed yet</span>;
  }
  const pills = answerOptionPills(question, a);
  if (pills) return <OptionPills pills={pills} />;
  if (Array.isArray(a)) return <span>{(a as string[]).join(', ')}</span>;
  if (typeof a === 'boolean') return <span>{a ? 'Yes' : 'No'}</span>;
  if (a === null || a === undefined || a === '') {
    return <span className="italic text-slate-400">Not answered yet</span>;
  }
  return <span>{String(a)}</span>;
}

// Read-only pill list for option-based questions: every offered option on its
// own row, with the company's selection(s) tinted in the brand accent (same
// idiom as StatusFilterTabs). Selection is also conveyed by the check glyph
// and sr-only text, not colour alone.
function OptionPills({ pills }: { pills: AnswerOptionPill[] }) {
  return (
    <div className="flex w-full flex-col items-start gap-1.5">
      {pills.map((p, i) => (
        <span
          key={i}
          className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5 text-xs font-medium ${
            p.selected ? '' : 'border-slate-200 bg-white text-slate-500'
          }`}
          style={
            p.selected
              ? {
                  color: 'var(--brand-accent)',
                  borderColor: 'var(--brand-accent)',
                  backgroundColor: 'color-mix(in srgb, var(--brand-accent) 12%, white)',
                }
              : undefined
          }
        >
          {p.selected && <span aria-hidden="true">✓</span>}
          {p.label}
          {p.selected && <span className="sr-only">(selected)</span>}
        </span>
      ))}
    </div>
  );
}

// ----------------------------------------------------------------------
// Company information panel. Read-only by default; the panel-level Edit
// button beside the tab heading switches both cards — Details and
// Company Details — to inputs, saved together through a single PATCH.
// ----------------------------------------------------------------------

type CompanyInfoForm = {
  contractor_type: string;
  company_number: string;
  contract_number: string;
  team_region: string;
  abn: string;
  trustee_info: string;
  primary_contact_name: string;
  contact_role: string;
  contact_phone: string;
  contact_email: string;
  employee_count: string;
  legal_entity_name: string;
  address_state: string;
  address_postcode: string;
  suburb: string;
};

// Every field except employee_count is sent as trimmed text (empty → null);
// employee_count is parsed to an integer separately in the save mutation.
const infoTextFields = [
  'contractor_type',
  'company_number',
  'contract_number',
  'team_region',
  'abn',
  'trustee_info',
  'primary_contact_name',
  'contact_role',
  'contact_phone',
  'contact_email',
  'legal_entity_name',
  'address_state',
  'address_postcode',
  'suburb',
] as const;

function toCompanyInfoForm(c: Company): CompanyInfoForm {
  return {
    contractor_type: c.contractor_type ?? '',
    company_number: c.company_number ?? '',
    contract_number: c.contract_number ?? '',
    team_region: c.team_region ?? '',
    abn: c.abn ?? '',
    trustee_info: c.trustee_info ?? '',
    primary_contact_name: c.primary_contact_name ?? '',
    contact_role: c.contact_role ?? '',
    contact_phone: c.contact_phone ?? '',
    contact_email: c.contact_email ?? '',
    employee_count: c.employee_count === null ? '' : String(c.employee_count),
    legal_entity_name: c.legal_entity_name ?? '',
    address_state: c.address_state ?? '',
    address_postcode: c.address_postcode ?? '',
    suburb: c.suburb ?? '',
  };
}

function CompanyInfoPanel({ company }: { company: Company }) {
  const qc = useQueryClient();
  const { branding } = useBranding();
  const contractorTypes = contractorTypeOptions(branding);
  const teamRegions = teamRegionNames(useTeamRegions().data);
  const [form, setForm] = useState<CompanyInfoForm>(() => toCompanyInfoForm(company));
  const [error, setError] = useState<unknown>(null);
  const [savedAt, setSavedAt] = useState<number | null>(null);
  // Both cards start read-only; the Edit button beside the heading switches
  // in the inputs.
  const [editing, setEditing] = useState(false);

  useEffect(() => {
    setForm(toCompanyInfoForm(company));
  }, [company.id, company.updated_at]);

  // Dirty when any field differs from the company record's current values.
  const baseline = toCompanyInfoForm(company);
  const dirty =
    infoTextFields.some((key) => form[key] !== baseline[key]) ||
    form.employee_count !== baseline.employee_count;

  const saveMutation = useMutation({
    mutationFn: () => {
      const payload: Record<string, string | number | null> = {};
      for (const key of infoTextFields) {
        const trimmed = form[key].trim();
        payload[key] = trimmed.length === 0 ? null : trimmed;
      }
      // Mirrors the company portal's handling: blank clears the value, anything
      // else must parse to a non-negative integer (sync throws land in onError).
      const empCount = form.employee_count.trim();
      if (empCount === '') {
        payload.employee_count = null;
      } else {
        const n = Number.parseInt(empCount, 10);
        if (!Number.isFinite(n) || n < 0) {
          throw new Error('# of employees must be a non-negative number');
        }
        payload.employee_count = n;
      }
      return adminApi.patch<{ company: Company }>(`/api/companies/${company.id}`, payload);
    },
    onSuccess: () => {
      setError(null);
      setSavedAt(Date.now());
      setEditing(false);
      void qc.invalidateQueries({ queryKey: ['company', company.id] });
    },
    onError: (err) => setError(err),
  });

  function set<K extends keyof CompanyInfoForm>(key: K, value: CompanyInfoForm[K]) {
    setForm((f) => ({ ...f, [key]: value }));
  }

  const post = useCallback(<T,>(p: string, b?: unknown) => adminApi.post<T>(p, b), []);
  const abnLookup = useAbnLookup(form.abn, post, '/api/companies/abn-lookup');
  const lastFilled = useRef<string | null>(null);
  useEffect(() => {
    if (abnLookup.status !== 'done' || !abnLookup.result.ok) return;
    const r = abnLookup.result;
    if (lastFilled.current === r.abn) return;
    lastFilled.current = r.abn;
    setForm((f) => ({
      ...f,
      legal_entity_name: r.legalEntityName ?? f.legal_entity_name,
      address_state: r.addressState ?? f.address_state,
      address_postcode: r.addressPostcode ?? f.address_postcode,
    }));
  }, [abnLookup]);

  // Cancel discards in-progress edits, clears any error, and returns to the
  // read-only view.
  function cancelEdit() {
    setForm(toCompanyInfoForm(company));
    setError(null);
    saveMutation.reset();
    setEditing(false);
  }

  return (
    <div>
      {/* Heading row carries the panel-level controls: Edit when read-only,
          Cancel/Save while editing. One Save writes both cards' fields. */}
      <TabHeading
        title="Company information"
        subtitle="Details you manage and information the company provided."
        actions={
          editing ? (
            <>
              <Btn
                size="sm"
                variant="secondary"
                onClick={cancelEdit}
                disabled={saveMutation.isPending}
              >
                Cancel
              </Btn>
              <Btn
                size="sm"
                onClick={() => saveMutation.mutate()}
                loading={saveMutation.isPending}
                disabled={!dirty}
              >
                Save
              </Btn>
            </>
          ) : (
            <>
              {savedAt !== null && <span className="text-xs text-emerald-600">Saved</span>}
              <Btn size="sm" variant="secondary" onClick={() => setEditing(true)}>
                Edit
              </Btn>
            </>
          )
        }
      />

      {error != null && (
        <div className="mb-4">
          <ErrorMessage error={error} />
        </div>
      )}

      <div className="grid items-start gap-4 md:grid-cols-2">
        {/* Details — admin-managed fields. */}
        <div className="rounded-xl border border-slate-200 bg-white p-5">
          <h3 className="mb-1 text-base font-semibold text-slate-800">Details</h3>
          {editing ? (
            <div>
              <InfoRow
                label={companyInfoLabel(branding, 'contractorType')}
                htmlFor="company-contractor-type"
              >
                {/* A saved value not in the list renders as the blank choice; it is
                    only overwritten if the admin picks an option and saves. */}
                <Select
                  id="company-contractor-type"
                  value={form.contractor_type}
                  onChange={(e) => set('contractor_type', e.target.value)}
                  disabled={contractorTypes.length === 0}
                >
                  <option value="">— Select —</option>
                  {contractorTypes.map((t) => (
                    <option key={t} value={t}>
                      {t}
                    </option>
                  ))}
                </Select>
                {contractorTypes.length === 0 && (
                  <p className="mt-1 text-xs text-slate-500">
                    Add contractor types in Settings to populate this dropdown.
                  </p>
                )}
              </InfoRow>
              <InfoRow
                label={companyInfoLabel(branding, 'companyNumber')}
                htmlFor="company-company-number"
              >
                <TextInput
                  id="company-company-number"
                  value={form.company_number}
                  onChange={(e) => set('company_number', e.target.value)}
                />
              </InfoRow>
              <InfoRow
                label={companyInfoLabel(branding, 'contractNumber')}
                htmlFor="company-contract-number"
              >
                <TextInput
                  id="company-contract-number"
                  value={form.contract_number}
                  onChange={(e) => set('contract_number', e.target.value)}
                />
              </InfoRow>
              <InfoRow
                label={companyInfoLabel(branding, 'teamRegion')}
                htmlFor="company-team-region"
              >
                <Select
                  id="company-team-region"
                  value={form.team_region}
                  onChange={(e) => set('team_region', e.target.value)}
                  disabled={teamRegions.length === 0}
                >
                  <option value="">— Select —</option>
                  {teamRegions.map((r) => (
                    <option key={r} value={r}>
                      {r}
                    </option>
                  ))}
                </Select>
                {teamRegions.length === 0 && (
                  <p className="mt-1 text-xs text-slate-500">
                    Add team regions in Settings to populate this dropdown.
                  </p>
                )}
              </InfoRow>
            </div>
          ) : (
            <div>
              <InfoRow label={companyInfoLabel(branding, 'contractorType')}>
                <ReadOnlyValue value={company.contractor_type} />
              </InfoRow>
              <InfoRow label={companyInfoLabel(branding, 'companyNumber')}>
                <ReadOnlyValue value={company.company_number} />
              </InfoRow>
              <InfoRow label={companyInfoLabel(branding, 'contractNumber')}>
                <ReadOnlyValue value={company.contract_number} />
              </InfoRow>
              <InfoRow label={companyInfoLabel(branding, 'teamRegion')}>
                <ReadOnlyValue value={company.team_region} />
              </InfoRow>
              <InfoRow label="Submitted">
                <ReadOnlyValue value={company.submitted_at ? new Date(company.submitted_at).toLocaleString() : null} />
              </InfoRow>
            </div>
          )}
        </div>

        {/* Company Details — values the company submitted, editable here too so
            an admin can correct them on the company's behalf. */}
        <div className="rounded-xl border border-slate-200 bg-white p-5">
          <h3 className="mb-1 text-base font-semibold text-slate-800">Company Details</h3>
          {editing ? (
            <div>
              <InfoRow label="ABN" htmlFor="company-abn">
                <TextInput
                  id="company-abn"
                  value={form.abn}
                  onChange={(e) => set('abn', e.target.value)}
                />
                <AbnStatusNote state={abnLookup} />
              </InfoRow>
              <InfoRow label="Legal entity name" htmlFor="company-legal-entity-name">
                <TextInput
                  id="company-legal-entity-name"
                  value={form.legal_entity_name}
                  onChange={(e) => set('legal_entity_name', e.target.value)}
                />
              </InfoRow>
              <InfoRow label="Suburb" htmlFor="company-suburb">
                <TextInput
                  id="company-suburb"
                  value={form.suburb}
                  onChange={(e) => set('suburb', e.target.value)}
                />
              </InfoRow>
              <InfoRow label="State" htmlFor="company-address-state">
                <TextInput
                  id="company-address-state"
                  value={form.address_state}
                  onChange={(e) => set('address_state', e.target.value)}
                />
              </InfoRow>
              <InfoRow label="Postcode" htmlFor="company-address-postcode">
                <TextInput
                  id="company-address-postcode"
                  value={form.address_postcode}
                  onChange={(e) => set('address_postcode', e.target.value)}
                />
              </InfoRow>
              <InfoRow label="Trustee Name and ACN" htmlFor="company-trustee-info">
                <TextInput
                  id="company-trustee-info"
                  value={form.trustee_info}
                  onChange={(e) => set('trustee_info', e.target.value)}
                />
              </InfoRow>
              <InfoRow
                label={companyInfoLabel(branding, 'primaryContact')}
                htmlFor="company-primary-contact"
              >
                <TextInput
                  id="company-primary-contact"
                  value={form.primary_contact_name}
                  onChange={(e) => set('primary_contact_name', e.target.value)}
                />
              </InfoRow>
              <InfoRow label="Contact role/position" htmlFor="company-contact-role">
                <TextInput
                  id="company-contact-role"
                  value={form.contact_role}
                  onChange={(e) => set('contact_role', e.target.value)}
                />
              </InfoRow>
              <InfoRow label="Contact phone" htmlFor="company-contact-phone">
                <TextInput
                  id="company-contact-phone"
                  type="tel"
                  value={form.contact_phone}
                  onChange={(e) => set('contact_phone', e.target.value)}
                />
              </InfoRow>
              <InfoRow label="Contact email" htmlFor="company-contact-email">
                <TextInput
                  id="company-contact-email"
                  type="email"
                  value={form.contact_email}
                  onChange={(e) => set('contact_email', e.target.value)}
                />
              </InfoRow>
              <InfoRow label="# of employees" htmlFor="company-employee-count">
                <TextInput
                  id="company-employee-count"
                  type="number"
                  min={0}
                  value={form.employee_count}
                  onChange={(e) => set('employee_count', e.target.value)}
                />
              </InfoRow>
            </div>
          ) : (
            <div>
              <InfoRow label="ABN">
                <div className="flex flex-wrap items-center gap-2">
                  <ReadOnlyValue value={company.abn} />
                  {company.abn && (
                    company.abn_verified
                      ? <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-xs font-medium text-emerald-700">✓ Verified</span>
                      : <span className="rounded-full bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-700">✗ Not verified</span>
                  )}
                </div>
              </InfoRow>
              <InfoRow label="Legal entity name">
                <ReadOnlyValue value={company.legal_entity_name} />
              </InfoRow>
              <InfoRow label="Suburb">
                <ReadOnlyValue value={company.suburb} />
              </InfoRow>
              <InfoRow label="State">
                <ReadOnlyValue value={company.address_state} />
              </InfoRow>
              <InfoRow label="Postcode">
                <ReadOnlyValue value={company.address_postcode} />
              </InfoRow>
              <InfoRow label="Trustee Name and ACN">
                <ReadOnlyValue value={company.trustee_info} />
              </InfoRow>
              <InfoRow label={companyInfoLabel(branding, 'primaryContact')}>
                <ReadOnlyValue value={company.primary_contact_name} />
              </InfoRow>
              <InfoRow label="Contact role/position">
                <ReadOnlyValue value={company.contact_role} />
              </InfoRow>
              <InfoRow label="Contact phone">
                <ReadOnlyValue value={company.contact_phone} />
              </InfoRow>
              <InfoRow label="Contact email">
                <ReadOnlyValue value={company.contact_email} />
              </InfoRow>
              <InfoRow label="# of employees">
                <ReadOnlyValue
                  value={company.employee_count === null ? null : String(company.employee_count)}
                />
              </InfoRow>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// One label/value row: label in a fixed-ish left column, arbitrary content
// (a read-only value or an input) on the right, with a divider between rows.
// Stacks on narrow screens; two columns at sm and up.
function InfoRow({
  label,
  htmlFor,
  children,
}: {
  label: string;
  htmlFor?: string;
  children: ReactNode;
}) {
  return (
    <div
      className="grid grid-cols-1 gap-1 border-b border-slate-100 py-3 last:border-b-0
                 sm:grid-cols-[minmax(160px,32%)_1fr] sm:items-center sm:gap-4"
    >
      {htmlFor ? (
        <label htmlFor={htmlFor} className="text-sm text-slate-500">
          {label}
        </label>
      ) : (
        <div className="text-sm text-slate-500">{label}</div>
      )}
      <div className="text-sm">{children}</div>
    </div>
  );
}

// Read-only value for the right column of an InfoRow: bold when present, an
// italic "Not provided" placeholder when empty.
function ReadOnlyValue({ value }: { value: string | null }) {
  return value && value.trim().length > 0 ? (
    <span className="font-medium text-slate-900">{value}</span>
  ) : (
    <span className="italic text-slate-400">Not provided</span>
  );
}
